import { IAzureBlobUploadInitDto, IInitializeAzureBlobUploadDto, IROVVideoCategory } from 'src/generated-types/rovvideo'
import { ResumableBlobUpload, uploadFileToBlob } from 'src/utils/azureBlobUpload'
import { deriveScreenshotFileName } from 'src/utils/rovVideoScreenshot'
import { buildCreateBlobVideoPayload, CreateBlobVideoPayload } from 'src/utils/rovVideoUploadPayload'
import { ResumableVimeoUpload } from 'src/utils/vimeoUpload'

export const MAX_CONCURRENT_UPLOADS = 3

export type UploadPhase = 'initialize' | 'upload' | 'create-record'

export type VideoEntryStatus = 'pending' | 'uploading' | 'done' | 'error' | 'paused'

export interface VideoEntry {
  id: string
  file: File
  title: string
  description: string
  mooringId: EntityId | null
  positionId: EntityId | null
  mainComponentId: EntityId | null
  status: VideoEntryStatus
  progress: number
  errorMessage?: string
  blobUploadUrl: string | null
  blobUrl: string | null
  blobName: string | null
  screenshotUploadUrl: string | null
  screenshotBlobUrl: string | null
  screenshotBlob: Blob | null
  screenshotPreviewUrl?: string | null
  vimeoUploadUrl: string | null
  vimeoId: string | null
  failedAtPhase: UploadPhase | null
}

export const createVideoEntry = (
  file: File,
  defaults: Pick<VideoEntry, 'mooringId' | 'positionId' | 'mainComponentId'>,
): VideoEntry => ({
  id: (crypto as any).randomUUID() as string,
  file,
  title: file.name.replace(/\.[^/.]+$/, ''),
  description: '',
  status: 'pending',
  progress: 0,
  blobUploadUrl: null,
  blobUrl: null,
  blobName: null,
  screenshotUploadUrl: null,
  screenshotBlobUrl: null,
  screenshotBlob: null,
  vimeoUploadUrl: null,
  vimeoId: null,
  failedAtPhase: null,
  ...defaults,
})

export async function runWithConcurrency<T>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<void>,
): Promise<void> {
  let index = 0
  const runNext = async (): Promise<void> => {
    while (index < items.length) {
      const current = items[index++]
      await worker(current)
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, runNext))
}

export const createConcurrencyLimiter = (limit: number) => {
  let active = 0
  const queue: Array<() => void> = []

  const acquire = (): Promise<void> =>
    new Promise(resolve => {
      if (active < limit) {
        active++
        resolve()
      } else {
        queue.push(resolve)
      }
    })

  const release = (): void => {
    active--
    const next = queue.shift()
    if (next) {
      active++
      next()
    }
  }

  return async function run<R>(task: () => Promise<R>): Promise<R> {
    await acquire()
    try {
      return await task()
    } finally {
      release()
    }
  }
}

export interface ApplyManualScreenshotSelectionDeps {
  videos: VideoEntry[]
  screenshotPromisesRef: React.MutableRefObject<Map<string, Promise<Blob | null>>>
  updateVideo: (id: string, updates: Partial<VideoEntry>) => void
}

export const applyManualScreenshotSelection = (
  videoId: string,
  blob: Blob,
  deps: ApplyManualScreenshotSelectionDeps,
): void => {
  const previous = deps.videos.find(v => v.id === videoId)
  if (previous?.screenshotPreviewUrl) URL.revokeObjectURL(previous.screenshotPreviewUrl)

  deps.screenshotPromisesRef.current.set(videoId, Promise.resolve(blob))
  deps.updateVideo(videoId, { screenshotPreviewUrl: URL.createObjectURL(blob) })
}

export interface ProcessBlobVideoDeps {
  category: IROVVideoCategory | null
  isShared: boolean
  customerId: EntityId
  localityId: EntityId
  screenshotPromise: Promise<Blob | null>
  initializeBlobUpload: (input: IInitializeAzureBlobUploadDto) => Promise<{ data: IAzureBlobUploadInitDto }>
  createROVVideo: (input: CreateBlobVideoPayload) => Promise<unknown>
  updateVideo: (id: string, updates: Partial<VideoEntry>) => void
  resumableUploadsRef: React.MutableRefObject<Map<string, ResumableBlobUpload | ResumableVimeoUpload>>
  onSuccess?: (video: VideoEntry) => void
}

export const processBlobVideo = async (video: VideoEntry, deps: ProcessBlobVideoDeps): Promise<void> => {
  const { updateVideo, resumableUploadsRef } = deps

  try {
    updateVideo(video.id, { status: 'uploading', errorMessage: undefined })

    const screenshotBlob = await deps.screenshotPromise
    updateVideo(video.id, { screenshotBlob })

    const initResponse = await deps.initializeBlobUpload({
      fileSize: video.file.size,
      fileName: video.file.name,
      screenshotFileName: screenshotBlob ? deriveScreenshotFileName(video.file.name) : undefined,
      title: video.title,
      description: video.description,
    })
    const initResult = initResponse.data

    if (!initResult?.uploadUrl) {
      updateVideo(video.id, {
        status: 'error',
        errorMessage: 'Failed to initialize blob upload',
        failedAtPhase: 'initialize',
      })
      return
    }

    updateVideo(video.id, {
      blobUploadUrl: initResult.uploadUrl,
      blobUrl: initResult.blobUrl,
      blobName: initResult.blobName,
    })

    const resumableUpload = new ResumableBlobUpload(video.file, initResult.uploadUrl, {
      onProgress: (loaded, total) => {
        const percentage = total > 0 ? Math.round((loaded / total) * 100) : 0
        updateVideo(video.id, { progress: percentage })
      },
    })
    resumableUploadsRef.current.set(video.id, resumableUpload)

    const screenshotUploadPromise =
      screenshotBlob && initResult.screenshotUploadUrl
        ? uploadFileToBlob(screenshotBlob, initResult.screenshotUploadUrl)
        : Promise.resolve()

    const [videoResult, screenshotResult] = await Promise.allSettled([resumableUpload.start(), screenshotUploadPromise])

    if (videoResult.status === 'rejected') {
      updateVideo(video.id, {
        status: 'error',
        errorMessage: videoResult.reason instanceof Error ? videoResult.reason.message : 'Upload failed',
        failedAtPhase: 'upload',
      })
      return
    }

    const screenshotUploadSucceeded = screenshotResult.status === 'fulfilled'
    if (screenshotBlob && !screenshotUploadSucceeded) {
      console.warn(`Screenshot upload failed for ${video.title}:`, (screenshotResult as PromiseRejectedResult).reason)
    }

    updateVideo(video.id, { progress: 100 })

    await deps.createROVVideo(
      buildCreateBlobVideoPayload(
        { title: video.title, description: video.description, category: deps.category, share: deps.isShared },
        initResult,
        screenshotUploadSucceeded,
        video.file.size,
        {
          mooringId: video.mooringId as EntityId,
          positionId: video.positionId as EntityId,
          mainComponentId: video.mainComponentId as EntityId,
          customerId: deps.customerId,
        },
      ),
    )

    updateVideo(video.id, { status: 'done', failedAtPhase: null })
    deps.onSuccess?.(video)
  } catch (error) {
    console.error(`Failed to upload video ${video.title}:`, error)
    updateVideo(video.id, {
      status: 'error',
      errorMessage: error instanceof Error ? error.message : 'Upload failed',
      failedAtPhase: video.failedAtPhase ?? 'initialize',
    })
  } finally {
    deps.resumableUploadsRef.current.delete(video.id)
  }
}
